import { Box, Button, Grid, InputLabel, TextField } from "@mui/material";
import React, { FC, useEffect } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { CITY_NAME_REGEX, NOT_ONLY_NUMBER_REGEX, PINCODE_REGEX } from "../../../utils/constants";
import { useEditSocietyConfigMutation, useUpdateSocietyConfigMutation } from "../../../redux/reducers/ots.reducer";
import { LoadingButton } from "@mui/lab";
import { useAppDispatch } from "../../../redux";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";
import { useNavigate } from "react-router-dom";
import NumberInput from "../../../common/NumberInput";
import { useGetSocietyConfigQuery } from "../../../redux/reducers/settings.reducer";

const CITY_NAME_MAX_CHARACTER_LENGTH = 200;
const SOCIETY_NAME_MAX_CHARACTER_LENGTH = 50;
const MAX_CHARACTER_LIMIT_ERROR_MESSAGE = "Maximum character limit exceeded";

const SocietyRegistration: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	// APIS
	const { data: societyConfigResponse } = useGetSocietyConfigQuery(null, { refetchOnMountOrArgChange: true });

	const [
		updateSocietyConfig,
		{
			isLoading: updateSocietyConfigLoading,
			error: updateSocietyConfigError,
			isSuccess: updateSocietyConfigSuccess,
			data: updateSocietyConfigResponse,
			reset: resetUpdateSocietyConfigState
		}
	] = useUpdateSocietyConfigMutation({});

	const [
		editSocietyConfig,
		{
			isLoading: editSocietyConfigLoading,
			error: editSocietyConfigError,
			isSuccess: editSocietyConfigSuccess,
			data: editSocietyConfigResponse,
			reset: resetEditSocietyConfigState
		}
	] = useEditSocietyConfigMutation();

	const {
		reset,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm();

	function handleBackButtonClick() {
		navigate(-1);
	}

	function handleSubmitForm(data: FieldValues) {
		if (societyConfigResponse && societyConfigResponse.results.length > 0) {
			editSocietyConfig({
				id: societyConfigResponse.results[0].id,
				data: {
					name: data.society_name,
					city: data.city_name,
					pincode: data.society_pincode,
					address_line1: data.address_line1,
					address_line2: data.address_line2,
					building_config: []
				}
			});
		} else {
			updateSocietyConfig({
				name: data.society_name,
				city: data.city_name,
				pincode: data.society_pincode,
				address_line1: data.address_line1,
				address_line2: data.address_line2,
				building_config: []
			});
		}
	}

	useEffect(() => {
		if (societyConfigResponse && societyConfigResponse.results.length > 0) {
			const societyDetails = societyConfigResponse.results[0];
			reset({
				society_name: societyDetails.name,
				city_name: societyDetails.city,
				society_pincode: societyDetails.pincode,
				address_line1: societyDetails.address_line1,
				address_line2: societyDetails.address_line2
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [societyConfigResponse]);

	useEffect(() => {
		if (updateSocietyConfigError && "data" in updateSocietyConfigError) {
			resetUpdateSocietyConfigState();

			dispatch(
				showFeedbackNotification({
					message: updateSocietyConfigError.data ? String(updateSocietyConfigError.data) : "Something went wrong",
					severity: "error"
				})
			);
		}
	}, [dispatch, resetUpdateSocietyConfigState, updateSocietyConfigError]);

	useEffect(() => {
		if (editSocietyConfigError && "data" in editSocietyConfigError) {
			resetEditSocietyConfigState();

			dispatch(
				showFeedbackNotification({
					message: editSocietyConfigError.data ? String(editSocietyConfigError.data) : "Something went wrong",
					severity: "error"
				})
			);
		}
	}, [dispatch, resetEditSocietyConfigState, editSocietyConfigError]);

	useEffect(() => {
		if (
			(updateSocietyConfigSuccess && updateSocietyConfigResponse) ||
			(editSocietyConfigSuccess && editSocietyConfigResponse)
		) {
			navigate("/ots/property-configuration");

			resetUpdateSocietyConfigState();
			resetEditSocietyConfigState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editSocietyConfigResponse, editSocietyConfigSuccess, updateSocietyConfigResponse, updateSocietyConfigSuccess]);

	return (
		<Box className="ots-screen-content-wrapper" component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
			<Box className="ots-form-wrapper">
				<Grid container rowSpacing={4} columnSpacing={6}>
					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="society-name-input"
							error={!!errors.society_name}
							classes={{ root: "ots-input-label-root" }}
						>
							Property Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="filled"
							color="primary"
							id="society-name-input"
							placeholder="Enter the Property Name"
							autoComplete="off"
							error={!!errors.society_name}
							helperText={
								errors.society_name && errors.society_name.message ? errors.society_name.message.toString() : ""
							}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("society_name", {
								required: "Property Name is required",
								pattern: { value: NOT_ONLY_NUMBER_REGEX, message: "Property Name cannot be only digits" },
								maxLength: { value: SOCIETY_NAME_MAX_CHARACTER_LENGTH, message: MAX_CHARACTER_LIMIT_ERROR_MESSAGE }
							})}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="city-name-input"
							error={!!errors.city_name}
							classes={{ root: "ots-input-label-root" }}
						>
							City Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="filled"
							color="primary"
							id="city-name-input"
							placeholder="Enter the City Name"
							autoComplete="off"
							error={!!errors.city_name}
							helperText={errors.city_name && errors.city_name.message ? errors.city_name.message.toString() : ""}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("city_name", {
								required: "City Name is required",
								pattern: { value: CITY_NAME_REGEX, message: "Please enter a valid City Name" },
								maxLength: { value: CITY_NAME_MAX_CHARACTER_LENGTH, message: MAX_CHARACTER_LIMIT_ERROR_MESSAGE }
							})}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="address-line1-name-input"
							error={!!errors.address_line1}
							classes={{ root: "ots-input-label-root" }}
						>
							Address Line 1
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="filled"
							color="primary"
							id="address-line1-name-input"
							placeholder="Enter the Address Line 1"
							autoComplete="off"
							error={!!errors.address_line1}
							helperText={
								errors.address_line1 && errors.address_line1.message ? errors.address_line1.message.toString() : ""
							}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("address_line1", { required: "Address Line 1 is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="address-line2-name-input"
							error={!!errors.address_line2}
							classes={{ root: "ots-input-label-root" }}
						>
							Address Line 2
						</InputLabel>

						<TextField
							fullWidth
							variant="filled"
							color="primary"
							id="address-line2-name-input"
							placeholder="Enter the Address Line 2"
							autoComplete="off"
							error={!!errors.address_line2}
							helperText={
								errors.address_line2 && errors.address_line2.message ? errors.address_line2.message.toString() : ""
							}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("address_line2")}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="society-pincode-input"
							error={!!errors.society_pincode}
							classes={{ root: "ots-input-label-root" }}
						>
							Property Pincode
						</InputLabel>

						<NumberInput
							required
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							color="primary"
							variant="filled"
							id="society-pincode-input"
							placeholder="Enter the Property Pincode"
							error={!!errors.society_pincode}
							helperText={
								errors.society_pincode && errors.society_pincode.message
									? errors.society_pincode.message.toString()
									: ""
							}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("society_pincode", {
								required: "Property Pincode is required",
								pattern: { value: PINCODE_REGEX, message: "Please enter a valid Pincode" },
								min: { value: 0, message: "Invalid pincode" }
							})}
						/>
					</Grid>
				</Grid>
			</Box>

			<Box className="ots-form-actions-wrapper">
				<Button variant="outlined" color="error" sx={{ paddingX: 5 }} onClick={handleBackButtonClick}>
					Back
				</Button>

				<LoadingButton
					disableElevation
					variant="contained"
					color="primary"
					type="submit"
					loading={updateSocietyConfigLoading || editSocietyConfigLoading}
				>
					Save and Next
				</LoadingButton>
			</Box>
		</Box>
	);
};

export default SocietyRegistration;
